import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_IMAGE, REMOTE_SUPPORT_URL } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <div className="overflow-hidden">
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: EASE }}
        className={className}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const cardImgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), {
    stiffness: 70,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), {
    stiffness: 70,
    damping: 18,
  });

  const onMouseMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="noise relative flex min-h-screen flex-col justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt=""
          className="h-[125%] w-full object-cover opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/30 to-[#0A0A0A]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid w-full max-w-[1440px] items-center gap-16 px-6 pb-28 pt-36 md:px-12 lg:grid-cols-12"
      >
        <div className="lg:col-span-8">
          <MaskedLine delay={0.15}>
            <p
              className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.35em] text-[#0033FF]"
              data-testid="hero-kicker"
            >
              <span className="inline-block h-px w-10 bg-[#0033FF]" />
              Opus Consulting, LLC
            </p>
          </MaskedLine>

          <h1 className="mt-8 font-display text-[clamp(3rem,8.5vw,6.5rem)] font-black uppercase leading-[0.92] tracking-tight">
            <MaskedLine delay={0.3}>
              <span>Database</span>
            </MaskedLine>
            <MaskedLine delay={0.42}>
              <span>
                Services <span className="text-[#0033FF]">&amp;</span>
              </span>
            </MaskedLine>
            <MaskedLine delay={0.54}>
              <span className="text-outline">IT Architecture</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.7}>
            <p
              className="mt-8 max-w-xl text-base leading-relaxed text-[#A0A0A0] md:text-lg"
              data-testid="hero-subtitle"
            >
              Data services and Virtual CIO services for accounting firms,
              company departments, and small businesses — reporting, custom
              database solutions, and data transfer done right.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-start-project-btn"
              className="group flex items-center gap-2 bg-[#0033FF] px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              Start a project
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>
            <a
              href={REMOTE_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-remote-support-btn"
              className="group flex items-center gap-2 border border-white/20 px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-[#0033FF] hover:text-[#0033FF]"
            >
              Remote Support
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>

        <div
          className="hidden lg:col-span-4 lg:block"
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={{ rotateX, rotateY }}
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 1.1, ease: EASE }}
            className="relative border border-white/10 bg-[#121212]/70 p-3 backdrop-blur-2xl"
            data-testid="hero-visual-card"
          >
            <div className="relative overflow-hidden">
              <motion.img
                style={{ y: cardImgY }}
                src={HERO_IMAGE}
                alt="Engineered data infrastructure"
                className="aspect-[4/5] w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent" />
            </div>
            <div className="flex items-center justify-between px-1 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">
              <span>SYS / OPUS-01</span>
              <span className="text-[#0033FF]">Online</span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-white/40 md:left-12"
        data-testid="hero-scroll-indicator"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-3.5 w-3.5 text-[#0033FF]" />
        </motion.span>
        Scroll
      </motion.div>
    </section>
  );
}
